'use client'

import { HStack, VStack } from '@/components/Atoms'
import { useIntlLocale } from '@/hooks/useIntlLocale'
import { Label, NumberField } from '@repo/ui/components'
import { useTranslation } from 'react-i18next'
import { Container } from './Container'

export type NumberRange = {
  min: number | undefined
  max: number | undefined
}

/**
 * How a range's bounds are typed and shown. `count` (the default) is a whole quantity: no separators,
 * no decimals. `currency` is a dollar amount: the symbol and thousands separators appear as the
 * amount is typed, cents allowed. `decimal` is for the rare fractional non-amount.
 */
export type NumberRangeFormat = 'currency' | 'count' | 'decimal'

type NumberRangeProps = {
  name: string
  title: string
  format?: NumberRangeFormat
  value: NumberRange
  setValue: React.Dispatch<React.SetStateAction<NumberRange>>
}

const FIELD_OPTIONS: Record<
  NumberRangeFormat,
  {
    format: 'currency' | 'decimal'
    minFractionDigits: number
    maxFractionDigits: number
    grouping: boolean
  }
> = {
  currency: { format: 'currency', minFractionDigits: 0, maxFractionDigits: 2, grouping: true },
  count: { format: 'decimal', minFractionDigits: 0, maxFractionDigits: 0, grouping: false },
  decimal: { format: 'decimal', minFractionDigits: 0, maxFractionDigits: 2, grouping: true },
}

export function NumberRange(props: NumberRangeProps) {
  const { t } = useTranslation('common')
  const locale = useIntlLocale()
  const options = FIELD_OPTIONS[props.format ?? 'count']
  const hasValue = props.value.min !== undefined || props.value.max !== undefined

  const handleReset = () => {
    props.setValue({ min: undefined, max: undefined })
  }

  return (
    <Container
      isResetVisible={hasValue}
      onReset={handleReset}
      hasOptionsSelected={hasValue}
      title={props.title}
    >
      <HStack gap="sm" className="w-full">
        <VStack gap="xs" className="flex-1">
          <Label htmlFor={`${props.name}-min`} className="text-muted-foreground text-xs">
            {t('numberRange.min')}
          </Label>
          <NumberField
            id={`${props.name}-min`}
            locale={locale}
            currency="CAD"
            {...options}
            className="bg-background"
            value={props.value.min ?? null}
            onValueChange={(next) => props.setValue({ ...props.value, min: next ?? undefined })}
          />
        </VStack>
        <VStack gap="xs" className="flex-1">
          <Label htmlFor={`${props.name}-max`} className="text-muted-foreground text-xs">
            {t('numberRange.max')}
          </Label>
          <NumberField
            id={`${props.name}-max`}
            locale={locale}
            currency="CAD"
            {...options}
            className="bg-background"
            value={props.value.max ?? null}
            onValueChange={(next) => props.setValue({ ...props.value, max: next ?? undefined })}
          />
        </VStack>
      </HStack>
    </Container>
  )
}
